import { useEffect } from 'react';
import { warmHeavyCaches } from '@/lib/utils/indexedDbCache';

/** Poll cadence. Each tick is a handful of IndexedDB reads while caches are fresh. */
const WARM_INTERVAL_MS = 30_000;
/** No pointer/keyboard activity for this long → the person left; stop spending BE calls. */
const IDLE_AFTER_MS = 10 * 60 * 1000;

let _lastActivityAt = Date.now();
let _activityListenersInstalled = false;

function installActivityListeners(): void {
  if (_activityListenersInstalled || typeof window === 'undefined') return;
  _activityListenersInstalled = true;
  const bump = () => { _lastActivityAt = Date.now(); };
  for (const type of ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const) {
    window.addEventListener(type, bump, { passive: true });
  }
}

/**
 * Proactive heavy-cache warming: every 30 s (and when the tab becomes visible again), ask
 * registered caches to re-read their well-known keys stale-first in the background
 * (`warmHeavyCaches`). Expired entries get refreshed *before* the user navigates to a page
 * that reads them, so the stale-first swap on navigation mostly disappears; fresh entries
 * cost only IndexedDB reads.
 *
 * Gated to keep BE load bounded: only while the tab is visible and the person has been
 * active in the last 10 minutes — an admin window left open over lunch stops warming.
 * The cost while active matches what the campaigns dashboard's own 30 s poll already does
 * on that page: one dashboard batch per 5-minute TTL per tab.
 */
export function useHeavyCacheWarming(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    installActivityListeners();

    let running = false;
    const tick = async () => {
      if (running) return;
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - _lastActivityAt > IDLE_AFTER_MS) return;
      running = true;
      try {
        await warmHeavyCaches();
      } finally {
        running = false;
      }
    };

    const intervalId = window.setInterval(() => void tick(), WARM_INTERVAL_MS);
    // Coming back to the tab is the moment a stale cache is most likely — warm right away.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void tick();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled]);
}
